import React, { useState, useEffect, useCallback } from 'react';
import {
  LuUser,
  LuClock,
  LuGlobe,
  LuBuilding2,
  LuPalette,
  LuSun,
  LuMoon,
  LuMonitor,
  LuCheck,
} from 'react-icons/lu';
import Autocomplete from '@/components/elements/AutoComplete';
import { getRole } from '@/constant/users/user.constant';
import { EAppTimezones, formatTimeByTimezone } from '@/utils/dateTime';
import { useAuth0 } from '@auth0/auth0-react';
import { SELECTED_TIMEZONE_LOCALSTORAGE_KEY } from '@/constant/global/global.constant';
import { PROFILE_PROPS } from '@/constant/profile/profile.const';
import Spinner from '@/components/atoms/Spinner';
import CommonDropdownSelector from '@/components/organism/GroupDropdown';
import { useTheme } from '@/context/theme.context';
import { AppButton } from '@/components/atoms';
import SegmentedControl from '@/components/elements/SegmentedControl';
import {
  GradientDivider,
  IconBadge,
  MetaChip,
  StatChip,
  SectionCard,
} from '@/components';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import { PageCard } from '@/components/organism/Cards/PageCard';
import { Text } from '@/components/atoms/AppText';
import { APP_BG, APP_BORDER, APP_HEX } from '@/constant/theme/colors.constant';
import { tid } from '@/utils/testId';
import { PROFILE_TEST_IDS as TID } from '@/constant/testIds/profile.testIds';


interface OAuthUser {
  name: string;
  nickname: string;
  family_name?: string;
  given_name?: string;
  picture?: string;
  updated_at: string;
  sub: string;
}

type ThemePreset = {
  name: string;
  primary: string;
  secondary: string;
  tertiary: string;
  mode: 'solid' | 'gradient';
};

// ── 6 solid presets ──────────────────────────────────────────────────────────
const SOLID_PRESETS: ThemePreset[] = [
  {
    name: 'Cantaloupe',
    primary: APP_HEX.primary,
    secondary: APP_HEX.primary,
    tertiary: APP_HEX.primary,
    mode: 'solid',
  },
  {
    name: '365rm',
    primary: APP_HEX.rm365,
    secondary: APP_HEX.rm365,
    tertiary: APP_HEX.rm365,
    mode: 'solid',
  },
  {
    name: 'Emerald',
    primary: APP_HEX.success,
    secondary: APP_HEX.success,
    tertiary: APP_HEX.success,
    mode: 'solid',
  },
  {
    name: 'Rose',
    primary: APP_HEX.rose,
    secondary: APP_HEX.rose,
    tertiary: APP_HEX.rose,
    mode: 'solid',
  },
  {
    name: 'Violet',
    primary: APP_HEX.violet,
    secondary: APP_HEX.violet,
    tertiary: APP_HEX.violet,
    mode: 'solid',
  },
  {
    name: 'Slate',
    primary: APP_HEX.slate,
    secondary: APP_HEX.slate,
    tertiary: APP_HEX.slate,
    mode: 'solid',
  },
];

// ── 6 gradient presets ───────────────────────────────────────────────────────
const GRADIENT_PRESETS: ThemePreset[] = [
  {
    name: 'Cantaloupe',
    primary: APP_HEX.primary,
    secondary: APP_HEX.secondary,
    tertiary: APP_HEX.tertiary,
    mode: 'gradient',
  }, // amber → red-orange → magenta
  {
    name: '365rm',
    primary: APP_HEX.rm365,
    secondary: APP_HEX.rm365Secondary,
    tertiary: APP_HEX.rm365Tertiary,
    mode: 'gradient',
  }, // blue gradient
  {
    name: 'Royal',
    primary: APP_HEX.purple,
    secondary: APP_HEX.violet,
    tertiary: APP_HEX.indigo,
    mode: 'gradient',
  }, // purple → violet → deep violet
  {
    name: 'Ocean',
    primary: APP_HEX.sky,
    secondary: APP_HEX.cyan,
    tertiary: APP_HEX.teal,
    mode: 'gradient',
  }, // sky → cyan → teal
  {
    name: 'Forest',
    primary: APP_HEX.success,
    secondary: APP_HEX.green,
    tertiary: APP_HEX.lime,
    mode: 'gradient',
  }, // emerald → green → lime
  {
    name: 'Steel',
    primary: APP_HEX.slateMid,
    secondary: APP_HEX.slate,
    tertiary: APP_HEX.slateDark,
    mode: 'gradient',
  }, // slate → dark slate → darker slate
];

const COLOR_PRESETS: ThemePreset[] = [...SOLID_PRESETS, ...GRADIENT_PRESETS];

const Profile = () => {
  const role = getRole();
  const { user } = useAuth0();
  const { colors, setColors, theme, setTheme, isDark } = useTheme();
  const stringifiedUser = JSON.stringify(user);
  const userData: OAuthUser | null = user ? JSON.parse(stringifiedUser) : null;
  const spinnerDuration = 1000;

  const timezonesDropdownList = Object.entries(EAppTimezones).map(
    ([key, value], index) => ({
      id: (index + 1).toString(),
      label: key,
      value,
    }),
  );

  const storedSelectedTimeZone =
    localStorage.getItem(SELECTED_TIMEZONE_LOCALSTORAGE_KEY) ||
    EAppTimezones.EST;

  const initialTimeZone =
    timezonesDropdownList.find((tz) => tz?.value === storedSelectedTimeZone) ||
    timezonesDropdownList[1];

  const [timeZone, setTimeZone] = useState(initialTimeZone);
  const [isLoading, setLoading] = useState(false);
  const [msg, setmsg] = useState('');
  const [hasMounted, setHasMounted] = useState(false);
  const [themePresetView, setThemePresetView] = useState<'solid' | 'gradient'>(
    () =>
      colors.primary === colors.secondary &&
        colors.secondary === colors.tertiary
        ? 'solid'
        : 'gradient',
  );
  const [appliedPreset, setAppliedPreset] = useState<number>(() => {
    return COLOR_PRESETS.findIndex(
      (p) =>
        p.primary === colors.primary &&
        p.secondary === colors.secondary &&
        p.tertiary === colors.tertiary,
    );
  });

  // Re-sync appliedPreset if colors are changed externally
  useEffect(() => {
    const found = COLOR_PRESETS.findIndex(
      (p) =>
        p.primary === colors.primary &&
        p.secondary === colors.secondary &&
        p.tertiary === colors.tertiary,
    );
    setAppliedPreset(found);
  }, [colors.primary, colors.secondary, colors.tertiary]);
  const [imgError, setImgError] = useState(false);

  const name = userData
    ? userData.given_name && userData.family_name
      ? `${userData.given_name} ${userData.family_name}`
      : userData.name
    : 'User';

  const initials = name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => setLoading(false), spinnerDuration);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  const isActivePaletteSolid =
    colors.primary === colors.secondary && colors.secondary === colors.tertiary;

  useEffect(() => {
    setThemePresetView(isActivePaletteSolid ? 'solid' : 'gradient');
  }, [isActivePaletteSolid]);

  const formatted = formatTimeByTimezone(timeZone?.value, userData?.updated_at);

  const handleTimeZoneSelection = (selectedTimeZone: any) => {
    if (!hasMounted) return;
    localStorage.setItem(
      SELECTED_TIMEZONE_LOCALSTORAGE_KEY,
      selectedTimeZone?.value || timezonesDropdownList[1]?.value,
    );
    setTimeZone(selectedTimeZone);
    setmsg(PROFILE_PROPS.SPINNER_PROPS.MESSAGE);
    setLoading(true);
  };

  const handleSelectionChange = () => {
    if (!hasMounted) return;
    setmsg(PROFILE_PROPS.SPINNER_PROPS.MESSAGEPO);
    setLoading(true);
  };

  const handleApplyPreset = useCallback(
    (idx: number) => {
      const preset = COLOR_PRESETS[idx];
      setColors({
        primary: preset.primary,
        secondary: preset.secondary,
        tertiary: preset.tertiary,
      });
      setAppliedPreset(idx);
    },
    [setColors],
  );

  const gradientStyle = isActivePaletteSolid
    ? colors.primary
    : `linear-gradient(135deg, ${colors.primary}, ${colors.secondary}, ${colors.tertiary})`;

  const gradientBg = isActivePaletteSolid
    ? { background: colors.primary }
    : {
      backgroundImage: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary}, ${colors.tertiary})`,
    };

  return (
    <div className="flex flex-col min-h-full">
      <PageCard testId={tid(TID.PAGE)} className={APP_BG.canvas}>
        {/* ── PROFILE HEADER ───────────────────────────────────────────────── */}
        <PageToolbar
          title={name}
          avatarNode={
            <div className="relative flex-shrink-0">
              <div
                className="w-12 h-12 rounded-2xl overflow-hidden flex items-center justify-center text-white text-base font-black"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                  boxShadow: `0 4px 14px ${colors.primary}55`,
                }}
              >
                {userData?.picture && !imgError ? (
                  <img
                    src={userData.picture}
                    alt={name}
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                  />
                ) : (
                  <span>{initials}</span>
                )}
              </div>
              <span
                className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 ${APP_BORDER.toolbarDot}`}
              />
            </div>
          }
          pills={
            <>
              <MetaChip dotColor="bg-emerald-400">
                {role || PROFILE_PROPS.USER_STATS.NO_ROLE}
              </MetaChip>
              {userData?.nickname && <MetaChip>@{userData.nickname}</MetaChip>}
            </>
          }
          rightContent={
            <>
              <StatChip
                icon={<LuUser className="w-5 h-5" />}
                label={PROFILE_PROPS.USER_STATS.NICKNAME_LABEL}
                value={
                  userData?.nickname || PROFILE_PROPS.USER_STATS.EMPTY_VALUE
                }
              />
              <StatChip
                icon={<LuClock className="w-5 h-5" />}
                label={PROFILE_PROPS.USER_STATS.LAST_LOGGED_ON_LABEL}
                value={formatted || PROFILE_PROPS.USER_STATS.EMPTY_VALUE}
              />
            </>
          }
        />

        {/* ── BODY ─────────────────────────────────────────────────────────── */}
        <div className="px-3 sm:px-4 lg:px-8 py-4 sm:py-6 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
          {/* ── LEFT COLUMN ──────────────────────────────── */}
          <div className="flex flex-col gap-5">
            {/* Timezone */}
            <SectionCard
              icon={<LuGlobe className="w-5 h-5" />}
              title={PROFILE_PROPS.SECTIONS.TIMEZONE_TITLE}
              subtitle={PROFILE_PROPS.SECTIONS.TIMEZONE_SUBTITLE}
            >
              <Autocomplete
                label={PROFILE_PROPS.TIMEZONE.TIMEZONE}
                lists={timezonesDropdownList}
                handleSelectedValue={handleTimeZoneSelection}
                handleSearch={() => { }}
                placeholder={PROFILE_PROPS.TIMEZONE.SELECT_TIMEZONE_PLACEHOLDER}
                selectedValue={timeZone}
                nameKey="label"
                className="text-black"
                isCapitalised
                dataTestId={tid(TID.TIMEZONE_SELECT)}
              />
            </SectionCard>

            {/* Provider / Operator */}
            <SectionCard
              icon={<LuBuilding2 className="w-5 h-5" />}
              title={PROFILE_PROPS.SECTIONS.PROVIDER_OP_TITLE}
              subtitle={PROFILE_PROPS.SECTIONS.PROVIDER_OP_SUBTITLE}
            >
              <CommonDropdownSelector
                onSelectionChange={handleSelectionChange}
                showProvider
                showOperator
                showLocation={false}
                showMarket={false}
                dataTestId={tid(TID.PROVIDER_DROPDOWN)}
              />
            </SectionCard>
          </div>

          {/* ── RIGHT COLUMN ─────────────────────────────── */}
          <div className="flex flex-col gap-5">
            {/* Appearance */}
            <SectionCard
              icon={
                theme === 'system' ? (
                  <LuMonitor className="w-5 h-5" />
                ) : isDark ? (
                  <LuMoon className="w-5 h-5" />
                ) : (
                  <LuSun className="w-5 h-5" />
                )
              }
              title={PROFILE_PROPS.SECTIONS.APPEARANCE_TITLE}
              subtitle={PROFILE_PROPS.SECTIONS.APPEARANCE_SUBTITLE}
            >
              <div className="grid grid-cols-3 gap-2">
                {(
                  [
                    {
                      value: 'light',
                      icon: LuSun,
                      label: PROFILE_PROPS.THEME.LIGHT.LABEL,
                      desc: PROFILE_PROPS.THEME.LIGHT.DESC,
                    },
                    {
                      value: 'dark',
                      icon: LuMoon,
                      label: PROFILE_PROPS.THEME.DARK.LABEL,
                      desc: PROFILE_PROPS.THEME.DARK.DESC,
                    },
                    {
                      value: 'system',
                      icon: LuMonitor,
                      label: PROFILE_PROPS.THEME.SYSTEM.LABEL,
                      desc: PROFILE_PROPS.THEME.SYSTEM.DESC,
                    },
                  ] as const
                ).map(({ value, icon: Icon, label, desc }) => {
                  const active = theme === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      data-testid={
                        value === 'light'
                          ? tid(TID.THEME_LIGHT_BTN)
                          : value === 'dark'
                            ? tid(TID.THEME_DARK_BTN)
                            : tid(TID.THEME_SYSTEM_BTN)
                      }
                      onClick={() => setTheme(value)}
                      className={[
                        'relative flex flex-col items-center gap-1.5 sm:gap-2 py-3 sm:py-4 px-1 sm:px-2 rounded-2xl border-2 transition-all duration-200 select-none',
                        active
                          ? 'border-transparent shadow-lg'
                          : 'border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.03] hover:border-gray-300 dark:hover:border-white/[0.15] hover:bg-white dark:hover:bg-white/[0.06]',
                      ].join(' ')}
                      style={
                        active
                          ? {
                            background: `linear-gradient(135deg, ${colors.primary}18, ${colors.secondary}12, ${colors.tertiary}08)`,
                            borderColor: colors.primary,
                            boxShadow: `0 0 0 1px ${colors.primary}33, 0 8px 24px ${colors.primary}22`,
                          }
                          : {}
                      }
                    >
                      {/* Icon ring */}
                      <span
                        className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200"
                        style={
                          active
                            ? {
                              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                              boxShadow: `0 4px 12px ${colors.primary}44`,
                            }
                            : { background: 'transparent' }
                        }
                      >
                        <Icon
                          className={`w-4 h-4 ${active ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`}
                        />
                      </span>
                      <Text
                        as="span"
                        size="md"
                        weight="semibold"
                        tracking="wide"
                        className={
                          active
                            ? 'text-gray-800 dark:text-gray-100'
                            : 'text-gray-500 dark:text-gray-400'
                        }
                      >
                        {label}
                      </Text>
                      <Text
                        as="span"
                        size="xs"
                        weight="medium"
                        tracking="wider"
                        className={
                          active
                            ? 'text-gray-500 dark:text-gray-400'
                            : 'text-gray-400 dark:text-gray-600'
                        }
                      >
                        {desc}
                      </Text>
                      {active && (
                        <span
                          className="absolute top-2 right-2 w-4 h-4 rounded-full flex items-center justify-center"
                          style={{ background: colors.primary }}
                        >
                          <LuCheck
                            className="w-2.5 h-2.5 text-white"
                            strokeWidth={3}
                          />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </SectionCard>

            {/* App Theme */}
            <SectionCard
              icon={<LuPalette className="w-5 h-5" />}
              title={PROFILE_PROPS.SECTIONS.THEME_TITLE}
              subtitle={PROFILE_PROPS.SECTIONS.THEME_SUBTITLE}
            >
              {/* Solid / Gradient toggle + live swatch */}
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <SegmentedControl
                  testId={tid(TID.PALETTE_VIEW_SELECT)}
                  size="sm"
                  shape="pill"
                  variant="gradient"
                  options={[
                    {
                      value: 'solid',
                      label: PROFILE_PROPS.PALETTE.SOLID_LABEL,
                    },
                    {
                      value: 'gradient',
                      label: PROFILE_PROPS.PALETTE.GRADIENT_LABEL,
                    },
                  ]}
                  value={themePresetView}
                  onChange={(v) =>
                    setThemePresetView(v as 'solid' | 'gradient')
                  }
                />
                {/* Animated live swatch */}
                <div className="flex items-center gap-2">
                  <div
                    className="h-6 w-20 rounded-full shadow-md transition-all duration-500"
                    style={
                      isActivePaletteSolid
                        ? {
                          background: colors.primary,
                          boxShadow: `0 2px 10px ${colors.primary}55`,
                        }
                        : {
                          backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.secondary}, ${colors.tertiary})`,
                          boxShadow: `0 2px 10px ${colors.primary}44`,
                        }
                    }
                  />
                </div>
              </div>

              {/* 2×3 preset grid */}
              <div className="grid grid-cols-3 gap-2.5">
                {(themePresetView === 'solid'
                  ? SOLID_PRESETS
                  : GRADIENT_PRESETS
                ).map((preset, idx) => {
                  const globalIdx =
                    themePresetView === 'solid'
                      ? idx
                      : SOLID_PRESETS.length + idx;
                  const isActive = appliedPreset === globalIdx;
                  const bg =
                    preset.mode === 'solid'
                      ? preset.primary
                      : `linear-gradient(135deg, ${preset.primary}, ${preset.secondary}, ${preset.tertiary})`;
                  return (
                    <button
                      key={preset.name}
                      type="button"
                      data-testid={tid(TID.PRESET_BTN(preset.name))}
                      onClick={() => handleApplyPreset(globalIdx)}
                      className={[
                        'relative group flex flex-col items-center gap-1.5 sm:gap-2 p-2 sm:p-3 rounded-2xl border-2 transition-all duration-200 hover:-translate-y-0.5 select-none',
                        isActive
                          ? ''
                          : 'border-gray-200 dark:border-white/[0.07] hover:border-gray-300 dark:hover:border-white/[0.15]',
                      ].join(' ')}
                      style={
                        isActive
                          ? {
                            borderColor: preset.primary,
                            background: `${preset.primary}0f`,
                            boxShadow: `0 0 0 1px ${preset.primary}33, 0 6px 18px ${preset.primary}28`,
                          }
                          : {}
                      }
                    >
                      {/* Full-width swatch bar */}
                      <div
                        className="w-full h-8 rounded-xl shadow-sm group-hover:shadow-md transition-shadow duration-200"
                        style={{ background: bg }}
                      />
                      {/* Color dot row */}
                      <div className="flex gap-1.5">
                        {[
                          preset.primary,
                          preset.secondary,
                          preset.tertiary,
                        ].map((c, i) => (
                          <span
                            key={i}
                            className="w-2 h-2 rounded-full ring-1 ring-black/10"
                            style={{ background: c }}
                          />
                        ))}
                      </div>
                      <Text
                        as="span"
                        size="xs"
                        weight="bold"
                        color="muted"
                        tracking="widest"
                        className="uppercase"
                      >
                        {preset.name}
                      </Text>
                      {isActive && (
                        <span
                          className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center shadow-md"
                          style={{ background: preset.primary }}
                        >
                          <LuCheck
                            className="w-3 h-3 text-white"
                            strokeWidth={3}
                          />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <Text
                as="p"
                size="sm"
                color="muted"
                align="center"
                tracking="wide"
                className="mt-4"
              >
                {PROFILE_PROPS.PALETTE.NOTE}
              </Text>
            </SectionCard>
          </div>
        </div>
      </PageCard>

      {hasMounted && isLoading && (
        <div className="fixed inset-0 z-[9999] pointer-events-none flex items-end justify-center pb-8 px-4">
          {/* Subtle backdrop vignette */}
          <div className="absolute inset-0 bg-black/10 dark:bg-black/30 backdrop-blur-[2px] pointer-events-none" />

          {/* Toast card */}
          <div className="relative pointer-events-auto animate-in fade-in slide-in-from-bottom-5 duration-400 ease-out">
            <div
              className="relative overflow-hidden rounded-2xl shadow-2xl"
              style={{
                background: 'rgba(15,23,42,0.72)',
                border: '1px solid rgba(255,255,255,0.10)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                boxShadow: `0 0 0 1px rgba(255,255,255,0.06), 0 24px 48px rgba(0,0,0,0.35), 0 8px 20px ${colors.primary}33`,
              }}
            >
              <div className="flex items-center gap-4 px-5 py-4">
                {/* Spinner */}
                <div className="relative flex-shrink-0 flex items-center justify-center">
                  <div
                    className="absolute w-8 h-8 rounded-full opacity-25 blur-md"
                    style={{ background: colors.primary }}
                  />
                  <Spinner size="md" color="white" />
                </div>

                {/* Text block */}
                <div className="flex flex-col min-w-0">
                  <Text
                    as="span"
                    size="lg"
                    weight="semibold"
                    tracking="wide"
                    className="text-white/95 whitespace-nowrap leading-tight"
                  >
                    {msg || PROFILE_PROPS.SPINNER_PROPS.APPLYING_TEXT}
                  </Text>
                  <Text
                    as="span"
                    size="sm"
                    weight="medium"
                    tracking="wider"
                    className="uppercase mt-0.5 text-white/40"
                  >
                    {PROFILE_PROPS.SPINNER_PROPS.WAIT_TEXT}
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>
  )}
    </div>
  );
};

export default Profile;